// BillingRateChart.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Line } from 'react-chartjs-2';
import { apiFetch } from '../../utils/api';
import { CURRENT_YEAR, CURRENT_QUARTER } from '../../constants';
import { Logger } from '../../utils/logger';

const logger = new Logger('BillingRateChart');

/**
 * A dedicated component for displaying average billing rate data with time frame toggles.
 * This component handles its own data fetching and state management.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.employee - Employee object containing employee_id
 * @param {boolean} props.hideTitle - Whether to hide the title
 * @param {boolean} props.compact - Whether to display in compact mode
 * @param {boolean} props.hideToggle - Whether to hide the time frame toggle
 * @param {string} props.timeFrame - Time frame to use (if controlled externally)
 */
const BillingRateChart = ({ 
  employee, 
  hideTitle = false, 
  compact = false,
  hideToggle = false,
  timeFrame: externalTimeFrame = null
}) => {
  // State for time frame toggle and data
  const [internalTimeFrame, setInternalTimeFrame] = useState('current_year');
  
  // Use external timeFrame if provided, otherwise use internal state
  const timeFrame = externalTimeFrame || internalTimeFrame;
  const [billingRateData, setBillingRateData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('Current Year Average Billing Rate');
  
  // Format currency for display
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Calculate average billing rate
  const calculateAverageBillingRate = (data) => {
    if (!data || data.length === 0) return 0;
    
    // Filter out entries with null or zero rates
    const validRates = data.filter(item => item.rate !== null && item.rate !== 0);
    
    if (validRates.length === 0) return 0;
    
    // Calculate average using only valid rates
    return validRates.reduce((sum, item) => sum + item.rate, 0) / validRates.length;
  };
  
  // Fetch billing rate data based on time frame
  const fetchBillingRateData = useCallback(async () => {
    if (!employee?.employee_id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      logger.debug(`Fetching financial impact data with time_frame=${timeFrame}`);
      
      // Make authenticated API call
      const response = await apiFetch(
        `employees/${employee.employee_id}/financial-impact?period_type=quarterly&time_frame=${timeFrame}`
      );
      const responseData = await response.json();

      // Extract billing rate data (backend flattens to billing_rate after processing)
      const data = responseData?.billing_rate_data?.quarterly || responseData?.billing_rate || [];

      // If no data at all, surface the empty state rather than rendering zero bars
      if (data.length === 0) {
        setBillingRateData([]);
        const titleMap = {
          current_year: 'Current Year Average Billing Rate',
          last_4_quarters: 'Last 4 Quarters Average Billing Rate',
          last_year: 'Previous Year Average Billing Rate',
        };
        setTitle(titleMap[timeFrame] || 'Average Billing Rate');
        setIsLoading(false);
        return;
      }

      // Process data based on time frame
      let processedData = [];
      let chartTitle = '';

      switch (timeFrame) {
        case 'current_year':
          // Use the constant for current year
          const currentYear = CURRENT_YEAR;
          
          // Q1 → Q4 left to right (oldest to newest)
          const currentYearQuarters = [
            { period: `Q1 ${currentYear}`, rate: 0 },
            { period: `Q2 ${currentYear}`, rate: 0 },
            { period: `Q3 ${currentYear}`, rate: 0 },
            { period: `Q4 ${currentYear}`, rate: 0 }
          ];

          data.forEach(item => {
            if (item.period.includes(currentYear.toString())) {
              const quarterMatch = item.period.match(/Q(\d)/);
              if (quarterMatch) {
                const quarterIndex = parseInt(quarterMatch[1]) - 1;
                if (quarterIndex >= 0 && quarterIndex < 4) {
                  currentYearQuarters[quarterIndex].rate = item.rate;
                }
              }
            }
          });

          processedData = currentYearQuarters;
          chartTitle = 'Current Year Average Billing Rate';
          break;

        case 'last_4_quarters':
          const currentQuarter = CURRENT_QUARTER;
          const year = CURRENT_YEAR;

          // Build oldest→newest
          const chronologicalQuarters = [];
          for (let i = 3; i >= 0; i--) {
            let q = currentQuarter - i;
            let y = year;
            if (q <= 0) { q += 4; y -= 1; }
            chronologicalQuarters.push({ period: `Q${q} ${y}`, rate: 0 });
          }

          data.forEach(item => {
            const index = chronologicalQuarters.findIndex(q => q.period === item.period);
            if (index !== -1) chronologicalQuarters[index].rate = item.rate;
          });

          processedData = chronologicalQuarters;
          chartTitle = 'Last 4 Quarters Average Billing Rate';
          break;

        case 'last_year':
          const lastYear = CURRENT_YEAR - 1;

          // Q1 → Q4 left to right
          const lastYearQuarters = [
            { period: `Q1 ${lastYear}`, rate: 0 },
            { period: `Q2 ${lastYear}`, rate: 0 },
            { period: `Q3 ${lastYear}`, rate: 0 },
            { period: `Q4 ${lastYear}`, rate: 0 }
          ];

          data.forEach(item => {
            if (item.period.includes(lastYear.toString())) {
              const quarterMatch = item.period.match(/Q(\d)/);
              if (quarterMatch) {
                const quarterIndex = parseInt(quarterMatch[1]) - 1;
                if (quarterIndex >= 0 && quarterIndex < 4) {
                  lastYearQuarters[quarterIndex].rate = item.rate;
                }
              }
            }
          });

          processedData = lastYearQuarters;
          chartTitle = 'Previous Year Average Billing Rate';
          break;
          
        default:
          processedData = data.slice(0, 4); // Limit to 4 items
          chartTitle = 'Average Billing Rate';
      }
      
      setBillingRateData(processedData);
      setTitle(chartTitle);
      setIsLoading(false);
      
      logger.debug('Processed billing rate data:', processedData);
    } catch (error) {
      logger.error('Error fetching financial impact data:', error);
      setError('Failed to load billing rate data');
      setIsLoading(false);
    }
  }, [employee?.employee_id, timeFrame]);
  
  // Fetch data when employee or time frame changes
  useEffect(() => {
    fetchBillingRateData();
  }, [employee, timeFrame, fetchBillingRateData]);
  
  // Handle time frame change
  const handleTimeFrameChange = (event, newTimeFrame) => {
    if (newTimeFrame !== null) {
      setInternalTimeFrame(newTimeFrame);
    }
  };
  
  // Chart data
  const chartData = {
    labels: billingRateData.map(item => item.period),
    datasets: [
      {
        label: 'Billing Rate',
        data: billingRateData.map(item => item.rate),
        borderColor: '#BB86FC',
        backgroundColor: 'rgba(187, 134, 252, 0.1)',
        borderWidth: 2,
        pointBackgroundColor: '#BB86FC',
        pointBorderColor: '#BB86FC',
        pointHoverBackgroundColor: '#03DAC6',
        pointHoverBorderColor: '#03DAC6',
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.1,
        fill: false
      }
    ]
  };
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: false,
        grid: {
          color: '#444',
          borderDash: [3, 3]
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 12
          },
          stepSize: 5,
          callback: function(value) {
            return `$${value}`;
          }
        }
      },
      x: {
        grid: {
          color: '#444',
          borderDash: [3, 3]
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 12
          }
        }
      }
    },
    plugins: {
      tooltip: {
        backgroundColor: '#333',
        borderColor: '#555',
        borderWidth: 1,
        titleColor: '#fff',
        bodyColor: '#BB86FC',
        cornerRadius: 4,
        padding: 10,
        callbacks: {
          label: function(context) {
            return `Billing Rate: ${formatCurrency(context.raw)}`;
          }
        }
      },
      legend: {
        display: false
      }
    }
  };
  
  // Calculate average billing rate
  const averageBillingRate = calculateAverageBillingRate(billingRateData);
  
  // Adjust chart options for compact mode
  if (compact) {
    chartOptions.scales.x.ticks.font.size = 10;
    chartOptions.scales.y.ticks.font.size = 10;
    chartOptions.plugins.tooltip.titleFont = { size: 10 };
    chartOptions.plugins.tooltip.bodyFont = { size: 10 };
    chartOptions.plugins.tooltip.padding = 6;
    chartData.datasets[0].pointRadius = 3;
    chartData.datasets[0].pointHoverRadius = 4;
    chartData.datasets[0].borderWidth = 1.5;
  }

  return (
    <Box>
      {/* Toggle buttons - hidden in compact mode or when hideToggle is true */}
      {!compact && !hideToggle && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            value={timeFrame}
            exclusive
            onChange={handleTimeFrameChange}
            aria-label="time frame"
          >
            <ToggleButton value="current_year" aria-label="current_year">
              Current Year
            </ToggleButton>
            <ToggleButton value="last_4_quarters" aria-label="last_4_quarters">
              Last 4 Quarters
            </ToggleButton>
            <ToggleButton value="last_year" aria-label="last_year">
              Last Year
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      
      {/* Summary Card - simplified in compact mode */}
      {!compact ? (
        <Box sx={{ mb: 3 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" align="center">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, color: '#BB86FC' }}>
              {formatCurrency(averageBillingRate)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              per hour
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" align="center">
            Avg Billing Rate ({timeFrame === 'current_year' ? 'CY' : timeFrame === 'last_year' ? 'LY' : 'L4Q'}): {formatCurrency(averageBillingRate)}
          </Typography>
        </Box>
      )}
      
      {/* Billing Rate Chart */}
      <Paper 
        elevation={0}
        sx={{ 
          p: compact ? 1 : 2, 
          mb: compact ? 1 : 3,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {!compact && (
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Average Billing Rate
          </Typography>
        )}
        
        {isLoading ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Loading billing rate data...
          </Typography>
        ) : error ? (
          <Typography variant="body2" color="error" align="center">
            {error}
          </Typography>
        ) : billingRateData.length > 0 ? (
          <Box sx={{ height: compact ? 240 : 300 }}>
            <Line data={chartData} options={chartOptions} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center">
            No billing rate data available
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default BillingRateChart;
